import { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { api } from '../lib/api';
import type { SOSAlert } from '../lib/types';

const POLL_INTERVAL_IDLE = 60_000;  // 60s when no active SOS
const POLL_INTERVAL_ACTIVE = 5_000; // 5s when SOS is active
const isNative = Capacitor.isNativePlatform();

// Native SOS plugin — plays alarm on STREAM_ALARM (works on silent/vibrate/DND)
const SOSNotification = isNative
  ? registerPlugin<{
      showSOSAlert(opts: { title: string; body: string; busNumber: string }): Promise<{ shown: boolean }>;
      stopAlarm(): Promise<{ stopped: boolean }>;
    }>('SOSNotification')
  : null;

// Web Audio fallback for browsers (laptop admin)
function playWebAlarmSound(audioCtxRef: React.MutableRefObject<AudioContext | null>) {
  try {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;

    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'square';
      osc.frequency.setValueAtTime(880, now + i * 0.4);
      osc.frequency.setValueAtTime(1100, now + i * 0.4 + 0.1);

      gain.gain.setValueAtTime(0.3, now + i * 0.4);
      gain.gain.setValueAtTime(0, now + i * 0.4 + 0.2);

      osc.start(now + i * 0.4);
      osc.stop(now + i * 0.4 + 0.2);
    }
  } catch { /* ignore */ }
}

export function SOSAlertMonitor({ onNavigate }: { onNavigate: (page: string) => void }) {
  const [activeAlert, setActiveAlert] = useState<SOSAlert | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const audioCtxRef = useRef<AudioContext | null>(null);
  const alarmIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAlertIdRef = useRef<string | null>(null);
  const nativeAlarmActive = useRef(false);
  const alarmTriggeredForId = useRef<string | null>(null); // Prevent re-triggering same alarm

  const checkForSOS = useCallback(async () => {
    try {
      const { sos_alerts } = await api.getSOSAlerts('active');
      const newAlert = sos_alerts.find(a => !dismissed.has(a.id));
      if (newAlert && newAlert.id !== lastAlertIdRef.current) {
        setActiveAlert(newAlert);
        lastAlertIdRef.current = newAlert.id;
      } else if (!newAlert) {
        setActiveAlert(null);
        lastAlertIdRef.current = null;
      }
    } catch { /* polling failure is non-critical */ }
  }, [dismissed]);

  // Poll for active SOS alerts — adaptive interval
  useEffect(() => {
    checkForSOS();
    const interval = setInterval(
      checkForSOS,
      activeAlert ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE
    );
    return () => clearInterval(interval);
  }, [checkForSOS, activeAlert]);

  // Listen for service worker SOS messages (instant, no polling delay)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'SOS_ALERT') {
        checkForSOS();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [checkForSOS]);

  // Start alarm when alert is active — only once per alert ID
  useEffect(() => {
    if (activeAlert && alarmTriggeredForId.current !== activeAlert.id) {
      alarmTriggeredForId.current = activeAlert.id;

      if (isNative && SOSNotification) {
        // Native: alarm plays on STREAM_ALARM — works on silent/vibrate/DND
        SOSNotification.showSOSAlert({
          title: `EMERGENCY SOS — ${activeAlert.bus_number}`,
          body: activeAlert.message || 'Driver needs help!',
          busNumber: activeAlert.bus_number || '',
        }).catch(() => {});
        nativeAlarmActive.current = true;
      } else {
        // Web: Web Audio API beeps (for laptop browser)
        playWebAlarmSound(audioCtxRef);
        alarmIntervalRef.current = setInterval(() => {
          playWebAlarmSound(audioCtxRef);
        }, 3000);
      }
    }
    return () => {
      if (alarmIntervalRef.current) clearInterval(alarmIntervalRef.current);
    };
  }, [activeAlert]);

  const stopAllAlarms = () => {
    if (alarmIntervalRef.current) clearInterval(alarmIntervalRef.current);
    alarmTriggeredForId.current = null;
    // Always try to stop native alarm (even if we think it's not active)
    if (isNative && SOSNotification) {
      SOSNotification.stopAlarm().catch(() => {});
      nativeAlarmActive.current = false;
    }
  };

  const handleAcknowledge = async () => {
    if (!activeAlert) return;
    try {
      await api.acknowledgesSOS(activeAlert.id);
    } catch { /* ignore */ }
    stopAllAlarms();
    setDismissed(prev => new Set(prev).add(activeAlert.id));
    setActiveAlert(null);
    lastAlertIdRef.current = null;
    onNavigate('safety');
  };

  const handleDismiss = () => {
    if (!activeAlert) return;
    stopAllAlarms();
    setDismissed(prev => new Set(prev).add(activeAlert.id));
    setActiveAlert(null);
    lastAlertIdRef.current = null;
  };

  if (!activeAlert) return null;

  return (
    <div className="sos-monitor-overlay">
      <div className="sos-monitor-card">
        <div className="sos-monitor-pulse" />
        <div className="sos-monitor-icon">🚨</div>
        <h1 className="sos-monitor-title">EMERGENCY SOS</h1>
        <div className="sos-monitor-bus">{activeAlert.bus_number}</div>
        <div className="sos-monitor-driver">Driver: {activeAlert.driver_name}</div>
        <div className="sos-monitor-message">{activeAlert.message || 'Driver triggered emergency SOS'}</div>
        {activeAlert.latitude && (
          <div className="sos-monitor-location">
            Location: {activeAlert.latitude.toFixed(5)}, {activeAlert.longitude?.toFixed(5)}
          </div>
        )}
        <div className="sos-monitor-time">
          {new Date(activeAlert.created_at).toLocaleTimeString()}
        </div>
        <div className="sos-monitor-actions">
          <button className="btn-danger sos-monitor-ack-btn" onClick={handleAcknowledge}>
            ACKNOWLEDGE & VIEW
          </button>
          <button className="btn-outline sos-monitor-dismiss-btn" onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
